import { ArrowDown, ArrowUp, Minus, Pause, Bot, AlertTriangle, RefreshCw, Loader2, ShieldAlert, TrendingUp, ShieldCheck, Flame, Gauge, GitBranch, History, Target, Zap, CheckCircle2, Circle, BellRing, BellOff, Clock } from "lucide-react";
import { api, fmtUsd } from "@/lib/api";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { SetupTimeline } from "./SetupTimeline";
import { MeaningBox } from "@/components/MeaningBox";
import { Hint, HintLabel } from "./Hint";
import { UpgradeButton } from "./Locked";
import { Lock } from "lucide-react";

const OUTLOOKS = {
  Bullish: { color: "#10B981", bg: "rgba(16,185,129,0.14)", border: "rgba(16,185,129,0.4)", icon: ArrowUp, pill: "pill pill-up" },
  Bearish: { color: "#EF4444", bg: "rgba(239,68,68,0.14)", border: "rgba(239,68,68,0.4)", icon: ArrowDown, pill: "pill pill-down" },
  Neutral: { color: "#F59E0B", bg: "rgba(245,158,11,0.14)", border: "rgba(245,158,11,0.4)", icon: Minus, pill: "pill pill-neutral" },
  Wait:    { color: "#8B5CF6", bg: "rgba(139,92,246,0.14)", border: "rgba(139,92,246,0.4)", icon: Pause,  pill: "pill" },
};

const RISK = {
  Low:      { color: "#10B981", icon: ShieldCheck },
  Moderate: { color: "#F59E0B", icon: ShieldAlert },
  High:     { color: "#F97316", icon: ShieldAlert },
  Extreme:  { color: "#EF4444", icon: Flame },
};

export const OutlookPill = ({ outlook }) => {
  const o = OUTLOOKS[outlook] || OUTLOOKS.Neutral;
  const Icon = o.icon;
  return (
    <Hint k="outlook">
    <span
      data-testid={`outlook-pill-${outlook}`}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold cursor-help"
      style={{ background: o.bg, color: o.color, border: `1px solid ${o.border}` }}
    >
      <Icon className="w-3 h-3" strokeWidth={2.5} />
      {outlook}
    </span>
    </Hint>
  );
};

export const StrengthBar = ({ value, outlook }) => {
  const o = OUTLOOKS[outlook] || OUTLOOKS.Neutral;
  const label = value >= 80 ? "Very Strong" : value >= 60 ? "Strong" : value >= 40 ? "Moderate" : value >= 20 ? "Weak" : "Very Weak";
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] font-mono-ticker uppercase tracking-widest text-slate-400 mb-1">
        <HintLabel k="strength"><span className="flex items-center gap-1"><Gauge className="w-3 h-3" /> Signal Strength</span></HintLabel>
        <span data-testid="signal-strength-value">{value}/100 · {label}</span>
      </div>
      <div className="h-2.5 bg-slate-900 rounded-full overflow-hidden border border-slate-800">
        <div
          className="h-full transition-all duration-500"
          style={{ width: `${value}%`, background: `linear-gradient(90deg, ${o.color}, ${o.color}aa)`, boxShadow: `0 0 12px ${o.color}66` }}
        />
      </div>
      <div className="text-[10px] text-slate-500 mt-1 italic">
        Signal Strength reflects agreement across indicators — not probability of profit.
      </div>
    </div>
  );
};

export const RiskBadge = ({ risk }) => {
  const r = RISK[risk] || RISK.Moderate;
  const Icon = r.icon;
  return (
    <Hint k="risk">
    <span className="inline-flex items-center gap-1 text-xs text-slate-200 cursor-help">
      <Icon className="w-3.5 h-3.5" style={{ color: r.color }} />
      <span className="opacity-70">Risk:</span>
      <span className="font-semibold" style={{ color: r.color }}>{risk}</span>
    </span>
    </Hint>
  );
};

const TimeframeCell = ({ label, data }) => {
  if (!data) return null;
  const o = OUTLOOKS[data.outlook] || OUTLOOKS.Neutral;
  return (
    <div
      data-testid={`timeframe-${label.toLowerCase().replace(/\s/g,'-')}`}
      className="rounded-lg p-3 border"
      style={{ borderColor: o.border, background: o.bg }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-400">{label}</span>
        <OutlookPill outlook={data.outlook} />
      </div>
      {data.note && <div className="text-xs text-slate-300 leading-snug">{data.note}</div>}
    </div>
  );
};

const Bullets = ({ items, color, icon: Icon, testid }) => {
  if (!items || items.length === 0) return <div className="text-xs text-slate-500 italic">No items</div>;
  return (
    <ul className="space-y-1.5" data-testid={testid}>
      {items.map((f, i) => (
        <li key={typeof f === "string" ? f : f.text || f.label || i} className="text-xs text-slate-300 flex items-start gap-2 leading-snug">
          <Icon className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color }} />
          <span>{f}</span>
        </li>
      ))}
    </ul>
  );
};

const SRTable = ({ sr }) => {
  if (!sr || (!sr.supports?.length && !sr.resistances?.length)) {
    return <div className="text-xs text-slate-500 italic">Insufficient history for S/R.</div>;
  }
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="support-resistance">
      <div>
        <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-emerald-400 mb-1">Support</div>
        <ul className="space-y-1">
          {(sr.supports || []).slice(0, 3).map((v, i) => (
            <li key={`s-${i}`} className="font-mono-ticker text-sm text-emerald-300">{fmtUsd(v)}</li>
          ))}
          {(sr.supports || []).length === 0 && <li className="text-xs text-slate-500">none nearby</li>}
        </ul>
      </div>
      <div>
        <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-red-400 mb-1">Resistance</div>
        <ul className="space-y-1">
          {(sr.resistances || []).slice(0, 3).map((v, i) => (
            <li key={`r-${i}`} className="font-mono-ticker text-sm text-red-300">{fmtUsd(v)}</li>
          ))}
          {(sr.resistances || []).length === 0 && <li className="text-xs text-slate-500">none nearby</li>}
        </ul>
      </div>
    </div>
  );
};

const SimilarPatterns = ({ data }) => {
  if (!data || !data.has_data) {
    return (
      <div className="text-xs text-slate-500 italic" data-testid="similar-patterns-empty">
        Not enough matching historical setups in this coin's price history to show a reliable comparison.
      </div>
    );
  }
  const Row = ({ label, s }) => (
    <div className="grid grid-cols-[90px_1fr] gap-2 items-center">
      <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-400">{label}</div>
      <div className="font-mono-ticker text-xs text-slate-200">
        avg <span className="font-semibold">{s.avg}%</span>
        <span className="text-slate-500 ml-2">median {s.median}%</span>
        <span className="text-emerald-400 ml-2">best {s.best}%</span>
        <span className="text-red-400 ml-2">worst {s.worst}%</span>
        <span className="text-slate-400 ml-2">↑ {s.positive_rate}%</span>
      </div>
    </div>
  );
  return (
    <div className="space-y-2" data-testid="similar-patterns">
      <div className="text-xs text-slate-400">
        Based on <span className="font-semibold text-slate-200">{data.matches}</span> past windows in this coin's own history where RSI was <span className="text-cyan-300">{data.rsi_zone}</span>, MACD histogram was {data.macd_positive ? "positive" : "negative"}, and price was {data.trend_up ? "above" : "below"} its SMA20.
      </div>
      <Row label="Next 7d" s={data.forward_7d} />
      <Row label="Next 30d" s={data.forward_30d} />
      <div className="text-[10px] text-slate-500 italic">
        Historical pattern stats are computed from CoinGecko price data. Past performance does not guarantee future results.
      </div>
    </div>
  );
};

const WaitingFor = ({ signal, onEvaluated }) => {
  const [conditions, setConditions] = useState(signal.waiting_conditions || []);
  const [events, setEvents] = useState(signal.condition_events || []);
  const [armed, setArmed] = useState(false);
  const [reeval, setReeval] = useState(false);
  const [armLoading, setArmLoading] = useState(false);

  useEffect(() => {
    setConditions(signal.waiting_conditions || []);
    setEvents(signal.condition_events || []);
    // Check if user already armed an alert for this signal
    api.get("/alerts").then((r) => {
      const found = (r.data || []).some((a) => a.kind === "signal_conditions" && a.signal_id === signal.id);
      setArmed(found);
    }).catch((e) => console.warn("[apex] request failed:", e?.message || e));
  }, [signal]);

  if (!conditions || conditions.length === 0) return null;

  const metCount = conditions.filter((c) => c.met).length;
  const total = conditions.length;
  const pctMet = total ? (metCount / total) * 100 : 0;

  const refresh = async () => {
    setReeval(true);
    try {
      const r = await api.post(`/signal/${signal.id}/evaluate`);
      setConditions(r.data.waiting_conditions || []);
      if (r.data.condition_events) setEvents(r.data.condition_events);
      if (r.data.newly_met?.length) {
        toast.success(`${r.data.newly_met.length} new condition${r.data.newly_met.length > 1 ? "s" : ""} met`);
      } else {
        toast.message("Conditions re-evaluated");
      }
      onEvaluated?.(r.data);
    } catch {
      toast.error("Could not re-check conditions");
    }
    setReeval(false);
  };

  const toggleArm = async () => {
    setArmLoading(true);
    try {
      if (armed) {
        await api.delete(`/signal/${signal.id}/conditions-alert`);
        setArmed(false);
        toast.message("Alert disarmed");
      } else {
        await api.post(`/signal/${signal.id}/conditions-alert`);
        setArmed(true);
        toast.success("Alert armed — we'll notify you when the setup changes");
      }
    } catch {
      toast.error("Could not update alert");
    }
    setArmLoading(false);
  };

  return (
    <div className="pt-2 border-t border-slate-800" data-testid="waiting-for-section">
      <div className="flex items-center gap-2 mb-2">
        <Clock className="w-4 h-4 text-violet-300" />
        <Hint k="waiting_for" side="left"><h4 className="font-display text-sm font-semibold cursor-help">What EV-IDEX Is Waiting For</h4></Hint>
        <span className="ml-auto text-[10px] font-mono-ticker uppercase tracking-widest text-slate-400">
          <span data-testid="waiting-met-count">{metCount}</span>/{total} met
        </span>
      </div>
      <div className="h-1.5 bg-slate-900 rounded-full overflow-hidden border border-slate-800 mb-3">
        <div
          data-testid="waiting-progress"
          className="h-full transition-all duration-500"
          style={{ width: `${pctMet}%`, background: "linear-gradient(90deg,#8B5CF6,#06B6D4)", boxShadow: "0 0 10px rgba(139,92,246,0.4)" }}
        />
      </div>
      <ul className="space-y-2" data-testid="waiting-conditions-list">
        {conditions.map((c) => (
          <li
            key={c.id || c.kind}
            data-testid={`condition-${c.kind}`}
            className={`rounded-lg p-2.5 border text-xs flex gap-2.5 items-start transition-colors ${
              c.met ? "bg-emerald-950/25 border-emerald-900/50" : "bg-slate-900/50 border-slate-800"
            }`}
          >
            {c.met ? (
              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400 mt-0.5" data-testid={`condition-met-${c.kind}`} />
            ) : (
              <Circle className="w-4 h-4 shrink-0 text-slate-500 mt-0.5" />
            )}
            <div className="min-w-0 flex-1">
              <div className={`font-medium ${c.met ? "text-emerald-200" : "text-slate-200"}`}>{c.label}</div>
              <div className="flex gap-3 flex-wrap mt-1 text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">
                <span>Current: <span className="text-slate-200">{formatConditionValue(c, c.current)}</span></span>
                <span>Target: <span className="text-cyan-300">{c.operator} {formatConditionValue(c, c.target)}</span></span>
                {c.metric && <span className="text-slate-500">· {c.metric}</span>}
              </div>
              {c.hint && <div className="text-[11px] text-slate-500 mt-1 leading-snug">{c.hint}</div>}
            </div>
          </li>
        ))}
      </ul>
      <div className="flex gap-2 mt-3">
        <Hint k="recheck" asChild>
        <button
          data-testid="recheck-conditions-btn"
          onClick={refresh}
          disabled={reeval}
          className="btn-ghost rounded-lg px-3 py-2 text-xs flex items-center gap-1.5 disabled:opacity-60"
        >
          {reeval ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Re-check
        </button>
        </Hint>
        <Hint k="arm_alert" asChild>
        <button
          data-testid="arm-alert-btn"
          onClick={toggleArm}
          disabled={armLoading}
          className="flex-1 rounded-lg px-3 py-2 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
          style={{
            background: armed ? "rgba(139,92,246,0.14)" : "rgba(6,182,212,0.14)",
            border: `1px solid ${armed ? "rgba(139,92,246,0.5)" : "rgba(6,182,212,0.5)"}`,
            color: armed ? "#c4b5fd" : "#67e8f9",
          }}
        >
          {armLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (armed ? <BellOff className="w-3.5 h-3.5" /> : <BellRing className="w-3.5 h-3.5" />)}
          {armed ? "Alert armed · tap to disarm" : "Alert Me When Conditions Are Met"}
        </button>
        </Hint>
      </div>
      <div className="text-[10px] text-slate-500 mt-2 italic leading-snug">
        Status, current and target values are calculated from live market data. No auto-trade is ever executed. Armed alerts are re-checked automatically every 15 minutes; we'll notify you in-app and on your phone (if enabled) so you can review the updated analysis.
      </div>
      <SetupTimeline events={events} createdAt={signal.created_at} />
    </div>
  );
};

function formatConditionValue(c, v) {
  if (v === null || v === undefined) return "—";
  // USD-shaped metrics
  if (/support|resistance|SMA|Close|BB|Bollinger/i.test(c.metric || "") || /Price|support|resistance|SMA|Bollinger/i.test(c.label || "")) {
    if (typeof v === "number" && Math.abs(v) >= 0.01) return fmtUsd(v);
  }
  if (typeof v === "number") return v.toFixed(Math.abs(v) < 1 ? 4 : 2);
  return String(v);
}

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export const SignalReport = ({ signal, loading, onRefresh, onPaperTrade, onEvaluated }) => {
  if (signal?.delayed && !loading) {
    return (
      <div className="apx-card p-5 border-amber-900/60" data-testid="ai-signal-card">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-base font-semibold">Signal Intelligence</div>
          <button data-testid="refresh-signal-btn" onClick={onRefresh} className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Retry</button>
        </div>
        <div className="rounded-lg border border-amber-800/60 bg-amber-950/30 p-4" data-testid="signal-data-delayed">
          <div className="font-mono-ticker text-xs tracking-widest text-amber-300 font-semibold">DATA DELAYED</div>
          <div className="text-sm text-amber-100 mt-1">Analysis temporarily unavailable until current market data is received.</div>
          <div className="text-[11px] font-mono-ticker text-amber-200/80 mt-2" data-testid="signal-data-as-of">Data as of {fmtTime(signal.as_of)}{signal.age_minutes != null ? ` · ${Math.round(signal.age_minutes)} min old` : ""}</div>
        </div>
      </div>
    );
  }
  if (!signal && !loading) {
    return (
      <div className="apx-card p-5" data-testid="ai-signal-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-cyan-500/15 border border-cyan-900/60">
              <Bot className="w-4 h-4 text-cyan-300" />
            </div>
            <div>
              <div className="font-display text-base font-semibold">Signal Intelligence</div>
              <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">GPT-6 Sol · EV-IDEX</div>
            </div>
          </div>
          <Hint k="analyze" side="left" asChild>
          <button
            data-testid="refresh-signal-btn"
            onClick={onRefresh}
            disabled={loading}
            className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Analyze
          </button>
          </Hint>
        </div>
        <div className="text-sm text-slate-400">Tap <span className="text-cyan-300">Analyze</span> to generate a Signal Intelligence Report grounded in live market data, technical indicators and historical pattern analysis.</div>
      </div>
    );
  }

  if (loading && !signal) {
    return (
      <div className="apx-card p-5 space-y-3" data-testid="ai-signal-card">
        <div className="skeleton h-6 w-40" />
        <div className="skeleton h-2 w-full" />
        <div className="skeleton h-24 w-full" />
        <div className="grid grid-cols-3 gap-2">
          <div className="skeleton h-16" /><div className="skeleton h-16" /><div className="skeleton h-16" />
        </div>
      </div>
    );
  }

  if (signal.insufficient) {
    return (
      <div className="apx-card p-5" data-testid="ai-signal-card">
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-950/30 border border-amber-900/50 text-amber-200 text-xs">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Insufficient data for reliable analysis</div>
            <div className="opacity-80">This asset lacks enough price history, liquidity, or reliable indicators.</div>
          </div>
        </div>
      </div>
    );
  }

  const outlook = signal.outlook || signal.stance || "Neutral";
  const strength = signal.signal_strength ?? signal.confidence ?? 0;
  const o = OUTLOOKS[outlook] || OUTLOOKS.Neutral;

  return (
    <div className="apx-card p-5 space-y-5" data-testid="ai-signal-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: o.bg, border: `1px solid ${o.border}` }}>
            <Bot className="w-4 h-4" style={{ color: o.color }} />
          </div>
          <div>
            <div className="font-display text-base font-semibold">Signal Intelligence</div>
            <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">GPT-6 Sol · EV-IDEX</div>
          </div>
        </div>
        <Hint k="analyze" side="left" asChild>
        <button
          data-testid="refresh-signal-btn"
          onClick={onRefresh}
          className="btn-ghost rounded-lg px-2.5 py-1.5 text-xs flex items-center gap-1.5"
          disabled={loading}
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Analyze
        </button>
        </Hint>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <OutlookPill outlook={outlook} />
        <RiskBadge risk={signal.risk} />
      </div>

      <StrengthBar value={strength} outlook={outlook} />

      {signal.summary && (
        <div className="text-sm text-slate-200 leading-relaxed" data-testid="signal-summary">{signal.summary}</div>
      )}
      <MeaningBox signal={signal} symbol={signal.symbol} price={signal.price} />

      {signal.timeframes && (signal.timeframes.short_term || signal.timeframes.swing || signal.timeframes.long_term) && (
        <div className="grid gap-2" data-testid="timeframe-grid">
          <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-400"><HintLabel k="timeframes">Timeframe Outlooks</HintLabel></div>
          <TimeframeCell label="Short-term" data={signal.timeframes.short_term} />
          <TimeframeCell label="Swing" data={signal.timeframes.swing} />
          <TimeframeCell label="Long-term" data={signal.timeframes.long_term} />
        </div>
      )}

      <div className="pt-2 border-t border-slate-800">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="w-4 h-4 text-emerald-400" />
          <Hint k="evidence_for" side="left"><h4 className="font-display text-sm font-semibold cursor-help">Why the AI sees this</h4></Hint>
        </div>
        <Bullets items={signal.evidence_for || signal.factors} color="#34d399" icon={Zap} testid="evidence-for" />
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500" data-testid="signal-transparency">
        <span data-testid="signal-generated-at">Generated {fmtTime(signal.created_at)}</span>
        {signal.price != null && <span data-testid="signal-analysis-price">· Price at analysis {fmtUsd(signal.price)}</span>}
        {(signal.series_as_of || signal.indicators?.series_as_of) && <span data-testid="signal-series-as-of">· Data as of {fmtTime(signal.series_as_of || signal.indicators.series_as_of)}</span>}
      </div>
      {signal.cached && (
        <div className="rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-[11px] text-slate-300 flex items-center gap-2" data-testid="signal-cached-notice">
          <RefreshCw className="w-3 h-3 text-slate-400" /> Cached analysis — Analyze for fresh evaluation.
        </div>
      )}
      {signal.plan_limited ? (
        <div className="rounded-lg border border-cyan-900/60 bg-cyan-950/20 p-4" data-testid="signal-plan-limited">
          <div className="flex items-center gap-2 text-sm font-semibold"><Lock className="w-4 h-4 text-cyan-300" /> Full Signal Intelligence is a Pro feature</div>
          <div className="text-xs text-slate-400 mt-1">You're seeing the headline call. Pro unlocks the complete report:</div>
          <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-300">
            {(signal.locked_sections || []).map((l) => <li key={l} className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-cyan-400" /> {l}</li>)}
          </ul>
          <div className="mt-3"><UpgradeButton plan="pro" testid="upgrade-signal_full" /></div>
        </div>
      ) : (
        <>
      <div className="pt-2 border-t border-slate-800">
        <div className="flex items-center gap-2 mb-2">
          <ShieldAlert className="w-4 h-4 text-red-400" />
          <Hint k="evidence_against" side="left"><h4 className="font-display text-sm font-semibold cursor-help">What could go wrong?</h4></Hint>
        </div>
        <Bullets items={signal.evidence_against} color="#f87171" icon={ShieldAlert} testid="evidence-against" />
      </div>

      <div className="pt-2 border-t border-slate-800">
        <div className="flex items-center gap-2 mb-2">
          <GitBranch className="w-4 h-4 text-cyan-400" />
          <Hint k="invalidation" side="left"><h4 className="font-display text-sm font-semibold cursor-help">What would change this signal?</h4></Hint>
        </div>
        <Bullets items={signal.invalidation} color="#67e8f9" icon={Target} testid="invalidation" />
      </div>

      <div className="pt-2 border-t border-slate-800">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-4 h-4 text-amber-300" />
          <Hint k="key_levels" side="left"><h4 className="font-display text-sm font-semibold cursor-help">Key levels (programmatic)</h4></Hint>
        </div>
        <SRTable sr={signal.support_resistance} />
      </div>

      <div className="pt-2 border-t border-slate-800">
        <div className="flex items-center gap-2 mb-2">
          <History className="w-4 h-4 text-violet-300" />
          <Hint k="similar" side="left"><h4 className="font-display text-sm font-semibold cursor-help">What happened in similar market conditions?</h4></Hint>
        </div>
        <SimilarPatterns data={signal.similar_patterns} />
      </div>

      {outlook === "Wait" && <WaitingFor signal={signal} onEvaluated={onEvaluated} />}
        </>
      )}

      {onPaperTrade && outlook !== "Wait" && (
        <Hint k="paper_trade" asChild>
        <button
          data-testid="paper-trade-from-signal-btn"
          onClick={() => onPaperTrade(outlook === "Bearish" ? "sell" : "buy")}
          className="w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
          style={{
            background: outlook === "Bearish" ? "rgba(239,68,68,0.12)" : "rgba(16,185,129,0.14)",
            border: `1px solid ${outlook === "Bearish" ? "rgba(239,68,68,0.5)" : "rgba(16,185,129,0.5)"}`,
            color: outlook === "Bearish" ? "#fca5a5" : "#6ee7b7",
          }}
        >
          {outlook === "Bearish" ? <ArrowDown className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
          Paper Trade {outlook === "Bearish" ? "Sell" : "Buy"} this signal
        </button>
        </Hint>
      )}

    </div>
  );
};
